import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Ticket } from 'lucide-react';
import { PublicEvent, clubsBookingApi } from '@/api/clubs-booking';
import { addPublicCartItem, getPublicCartItemsForEvent, PublicCartItem } from '@/utils/publicCart';
import PublicClubHeader from '@/components/public/PublicClubHeader';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { auth } from '@/store/authStore';
import { formatCurrencyFromCents } from '@/utils/currency';
import { getClubSiteHref } from '@/utils/publicSite';

const getSessionBookingCutoff = (session: NonNullable<PublicEvent['sessions']>[number]) => {
  const start = new Date(session.startDateTime);
  const cutoffHours = typeof session.bookingCutoffHours === 'number' ? session.bookingCutoffHours : undefined;
  if (cutoffHours !== undefined && Number.isFinite(cutoffHours) && cutoffHours >= 0) {
    return new Date(start.getTime() - (cutoffHours * 60 * 60 * 1000));
  }
  return start;
};

const isSessionBookableNow = (session: NonNullable<PublicEvent['sessions']>[number]) => {
  if (session.bookingEnabled === false) return false;
  const cutoff = getSessionBookingCutoff(session);
  if (Number.isNaN(cutoff.getTime())) return false;
  return Date.now() < cutoff.getTime();
};

const PublicBookingPage = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [specificSessionDraft, setSpecificSessionDraft] = useState<Record<string, string[]>>({});
  const [cartItemsForEvent, setCartItemsForEvent] = useState<PublicCartItem[]>([]);
  const [alreadyBookedCount, setAlreadyBookedCount] = useState(0);
  const [waitlistedSessionIds, setWaitlistedSessionIds] = useState<string[]>([]);
  const [joiningSessionId, setJoiningSessionId] = useState('');
  const [mobileTab, setMobileTab] = useState<'tickets' | 'details'>('details');

  useEffect(() => {
    const load = async () => {
      if (!eventSlug) return;
      setLoading(true);
      setError('');
      try {
        const data = await clubsBookingApi.getPublicEvent(eventSlug);
        setEvent(data);
        const user = auth.getUser();
        if (user?.isAuthenticated && user.role === 'parent') {
          const parentBookings = await clubsBookingApi.listParentBookings();
          const existingForActivity = (parentBookings || []).filter((booking) => booking.event?.id === data._id);
          setAlreadyBookedCount(existingForActivity.length);
          const waitlist = await clubsBookingApi.listParentWaitlist(data._id);
          setWaitlistedSessionIds(waitlist.sessionIds || []);
        } else {
          setAlreadyBookedCount(0);
          setWaitlistedSessionIds([]);
        }
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load this event');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [eventSlug]);

  useEffect(() => {
    if (!event?._id) {
      setCartItemsForEvent([]);
      return;
    }

    const syncCart = () => setCartItemsForEvent(getPublicCartItemsForEvent(event._id));
    syncCart();
    window.addEventListener('storage', syncCart);
    window.addEventListener('focus', syncCart);
    window.addEventListener('clubsbooking-cart-updated', syncCart);
    return () => {
      window.removeEventListener('storage', syncCart);
      window.removeEventListener('focus', syncCart);
      window.removeEventListener('clubsbooking-cart-updated', syncCart);
    };
  }, [event?._id]);

  const allSessionTickets = useMemo(() => {
    if (!event) return [];
    return (event.ticketTypes || []).filter((ticket) => ticket.mode === 'all_sessions');
  }, [event]);

  const availableSpecificTickets = useMemo(() => {
    if (!event) return [];
    return (event.ticketTypes || []).filter((ticket) => ticket.mode === 'specific_sessions');
  }, [event]);
  const spotsRemainingLabel = event && event.capacity < 0 ? 'Unlimited' : String(event?.spotsRemaining ?? 0);
  const isSessionFull = (session: NonNullable<PublicEvent['sessions']>[number]) =>
    session.capacity >= 0 && typeof session.spotsRemaining === 'number' && session.spotsRemaining <= 0;
const isSessionBookableForPurchase = (session: NonNullable<PublicEvent['sessions']>[number]) =>
  isSessionBookableNow(session) && !isSessionFull(session);
const getSessionAvailabilityLabel = (session: NonNullable<PublicEvent['sessions']>[number]) => {
  if (!isSessionBookableNow(session)) return 'Closed';
  if (isSessionFull(session)) return 'Full';
  if (session.capacity < 0) return 'Open';
  const remaining = typeof session.spotsRemaining === 'number'
    ? Math.max(0, session.spotsRemaining)
    : Math.max(0, session.capacity);
  return `${remaining} place${remaining === 1 ? '' : 's'} left`;
};
  const bookableSessionIds = useMemo(() => {
    if (!event?.sessions?.length) return [] as string[];
    return event.sessions
      .filter((session) => isSessionBookableNow(session) && !(session.capacity >= 0 && typeof session.spotsRemaining === 'number' && session.spotsRemaining <= 0))
      .map((session) => session.id);
  }, [event]);
  const hasAnyBookableSessions = bookableSessionIds.length > 0;
  const hasEventItemInCart = cartItemsForEvent.length > 0;
  const buildSelectionSignature = (ticketTypeId: string, sessionIds: string[]) =>
    `${ticketTypeId}::${Array.from(new Set(sessionIds.filter(Boolean))).sort().join('|')}`;
  const cartSignatures = useMemo(
    () => new Set(cartItemsForEvent.map((item) => buildSelectionSignature(item.ticketTypeId, item.sessionIds))),
    [cartItemsForEvent],
  );
  const isSelectionInCart = (ticketTypeId: string, sessionIds: string[]) => {
    const normalizedSessionIds = Array.from(new Set(sessionIds.filter(Boolean)));
    if (!normalizedSessionIds.length) return false;
    return cartSignatures.has(buildSelectionSignature(ticketTypeId, normalizedSessionIds));
  };

  const onJoinWaitlist = async (sessionId: string) => {
    if (!event) return;
    setJoiningSessionId(sessionId);
    setError('');
    setMessage('');
    try {
      const response = await clubsBookingApi.joinParentWaitlist(event._id, { sessionId });
      setWaitlistedSessionIds((prev) => (prev.includes(sessionId) ? prev : [...prev, sessionId]));
      setMessage(response.message || 'Added to waitlist.');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not join waitlist');
    } finally {
      setJoiningSessionId('');
    }
  };

  const onAddToCart = (ticketTypeId: string, sessionIds: string[]) => {
    if (!event) return false;
    const allowedSessionIds = Array.from(new Set(sessionIds.filter((sessionId) => bookableSessionIds.includes(sessionId))));
    if (!allowedSessionIds.length) {
      setError('Please select at least one available session.');
      return false;
    }
    const beforeCount = getPublicCartItemsForEvent(event._id).length;
    addPublicCartItem({
      id: crypto.randomUUID(),
      eventId: event._id,
      ticketTypeId,
      sessionIds: allowedSessionIds,
      quantity: 1,
      addedAt: new Date().toISOString(),
    });
    const afterCount = getPublicCartItemsForEvent(event._id).length;
    setCartItemsForEvent(getPublicCartItemsForEvent(event._id));
    setError('');
    setMessage(afterCount > beforeCount ? 'Added to cart.' : 'This ticket option is already in your cart.');
    return afterCount > beforeCount;
  };

  useEffect(() => {
    setSpecificSessionDraft((prev) => {
      let changed = false;
      const next: Record<string, string[]> = {};

      Object.entries(prev).forEach(([ticketTypeId, sessionIds]) => {
        const lockedSessionIds = new Set(
          cartItemsForEvent
            .filter((item) => item.ticketTypeId === ticketTypeId)
            .flatMap((item) => item.sessionIds),
        );
        const filtered = (sessionIds || []).filter((sessionId) => !lockedSessionIds.has(sessionId));
        if (filtered.length !== (sessionIds || []).length) {
          changed = true;
        }
        next[ticketTypeId] = filtered;
      });

      return changed ? next : prev;
    });
  }, [cartItemsForEvent]);

  if (loading) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6">Loading activity...</main>;
  }

  if (error && !event) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6 text-red-700">{error}</main>;
  }

  if (!event) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6">Activity not found.</main>;
  }

  const isParentLoggedIn = (() => {
    const user = auth.getUser();
    return !!(user.isAuthenticated && user.role === 'parent');
  })();

  const content = (
      <div className="mx-auto grid w-full gap-3 px-3 pb-8 pt-6 sm:px-4 md:px-8 lg:grid-cols-[1fr_460px]">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 lg:hidden">
          <div className="flex items-center justify-between gap-3">
            {event.clubProfile?.showClubTitle !== false && (
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#1F8F5F]">
                {event.clubProfile?.clubName || 'After-school club'}
              </p>
            )}
            <div className="flex items-center gap-2">
              {event.clubProfile?.publicSlug && (
                <a href={getClubSiteHref(event.clubProfile.publicSlug)} className="text-sm text-slate-600 hover:text-slate-900 hover:underline">
                  Back to club
                </a>
              )}
            </div>
          </div>
          <h1 className="mt-2 text-3xl font-semibold text-slate-900">{event.title}</h1>
          {event.subtitle && <p className="mt-2 text-slate-700">{event.subtitle}</p>}
          <p className="mt-3 max-w-3xl text-slate-600">{event.description || 'Join this activity and reserve your child\'s place online.'}</p>
        </section>

        <div className="lg:hidden">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1">
            <button
              type="button"
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                mobileTab === 'details'
                  ? 'bg-[#1F8F5F] text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              onClick={() => setMobileTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                mobileTab === 'tickets'
                  ? 'bg-[#1F8F5F] text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              onClick={() => setMobileTab('tickets')}
            >
              Tickets
            </button>
          </div>
        </div>

        <section className={`rounded-2xl border border-slate-200 bg-white p-6 md:p-8 ${mobileTab === 'details' ? 'block' : 'hidden'} lg:block`}>
          <div className="hidden lg:block">
            <div className="flex items-center justify-between gap-3">
              {event.clubProfile?.showClubTitle !== false && (
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#1F8F5F]">
                  {event.clubProfile?.clubName || 'After-school club'}
                </p>
              )}
              <div className="flex items-center gap-2">
                {event.clubProfile?.publicSlug && (
                  <a href={getClubSiteHref(event.clubProfile.publicSlug)} className="text-sm text-slate-600 hover:text-slate-900 hover:underline">
                    Back to club
                  </a>
                )}
              </div>
            </div>
            <h1 className="mt-2 text-3xl font-semibold text-slate-900 md:text-4xl">{event.title}</h1>
            {event.subtitle && <p className="mt-2 text-slate-700">{event.subtitle}</p>}
            <p className="mt-3 max-w-3xl text-slate-600">{event.description || 'Join this activity and reserve your child\'s place online.'}</p>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><MapPin className="h-4 w-4" />Venue</div>
              <p className="mt-2 text-sm font-medium text-slate-800">{event.location}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><Ticket className="h-4 w-4" />Availability</div>
              <p className="mt-2 text-sm font-medium text-slate-800">{spotsRemainingLabel} spots remaining</p>
            </div>
          </div>

          {!!event.sessions?.length && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-800">Dates & times</h3>
              <div className="mt-2 space-y-2">
                {event.sessions.map((session) => (
                  <div key={session.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <span>{new Date(session.startDateTime).toLocaleString('en-GB')} - {new Date(session.endDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${isSessionBookableForPurchase(session) ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {getSessionAvailabilityLabel(session)}
                      </span>
                      {isParentLoggedIn && event.waitlistEnabled && isSessionFull(session) && (
                        waitlistedSessionIds.includes(session.id) ? (
                          <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">On waitlist</span>
                        ) : (
                          <button
                            type="button"
                            className="rounded-md border border-[#D8F1E2] bg-[#F3FBF7] px-2 py-1 text-xs font-medium text-[#1F8F5F] hover:bg-[#EAF7F0] disabled:opacity-60"
                            onClick={() => void onJoinWaitlist(session.id)}
                            disabled={joiningSessionId === session.id}
                          >
                            {joiningSessionId === session.id ? 'Joining...' : 'Join waitlist'}
                          </button>
                        )
                      )}
                      {!isParentLoggedIn && event.waitlistEnabled && isSessionFull(session) && (
                        <span className="text-xs text-slate-500">Login to join waitlist</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!event.addOns?.length && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-800">Add-ons available at checkout</h3>
              <div className="mt-2 space-y-1 text-sm text-slate-700">
                {event.addOns.map((addOn) => (
                  <p key={addOn.id}>{addOn.name} (+{formatCurrencyFromCents(addOn.priceCents, event.currency)} per session)</p>
                ))}
              </div>
            </div>
          )}

          {!!event.automaticDiscounts?.length && (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <h3 className="text-sm font-semibold text-emerald-900">Automatic discounts available</h3>
              <div className="mt-2 space-y-1 text-sm text-emerald-800">
                {event.automaticDiscounts.map((discount) => (
                  <p key={discount.id}>{discount.summary}</p>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-[#1F8F5F] hover:underline"
            >
              Open venue in Google Maps
            </a>
          </div>
        </section>

        <section className={`rounded-2xl border border-slate-200 bg-white p-6 ${mobileTab === 'tickets' ? 'block' : 'hidden'} lg:block`}>
          <h2 className="text-xl font-semibold text-slate-900">Choose tickets</h2>
          <p className="mt-1 text-sm text-slate-500">Add a ticket option to your cart, then continue to checkout.</p>
          {!hasAnyBookableSessions && (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Not available to book.
            </div>
          )}
          {alreadyBookedCount > 0 && (
            <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
              You already have {alreadyBookedCount} booking{alreadyBookedCount === 1 ? '' : 's'} for this activity.
              <Link to="/my-bookings" className="ml-1 font-medium underline">View bookings</Link>
            </div>
          )}

          {!!allSessionTickets.length && (
            <div className="mt-5 rounded-xl border border-slate-200">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">All Sessions Bookings</div>
              <div className="space-y-2 p-3">
                {allSessionTickets.map((ticket) => {
                  const allowedForTicket = (ticket.sessionIds?.length ? ticket.sessionIds : (event.sessions || []).map((session) => session.id))
                    .filter((sessionId) => bookableSessionIds.includes(sessionId));
                  const selectionInCart = isSelectionInCart(ticket.id, allowedForTicket);

                  return (
                    <div key={ticket.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div>
                        <p className="font-medium text-slate-900">{ticket.name}</p>
                        <p className="text-sm text-slate-600">{formatCurrencyFromCents(ticket.priceCents, event.currency)}</p>
                        {!hasAnyBookableSessions && <p className="text-xs text-rose-700">Not available to book.</p>}
                        {selectionInCart && <p className="text-xs font-medium text-emerald-700">Already in cart</p>}
                      </div>
                      <button
                        type="button"
                        className={`rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                          selectionInCart ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-[#1F8F5F] hover:bg-[#1F8F5F]'
                        }`}
                        onClick={() => onAddToCart(ticket.id, allowedForTicket)}
                        disabled={!hasAnyBookableSessions || selectionInCart}
                      >
                        {selectionInCart ? 'In cart' : 'Add to cart'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!!availableSpecificTickets.length && (
            <div className="mt-4 rounded-xl border border-slate-200">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">Single Session Bookings</div>
              <div className="space-y-3 p-3">
                {availableSpecificTickets.map((ticket) => {
                  const selectedSessionIds = specificSessionDraft[ticket.id] || [];
                  const allowed = new Set(ticket.sessionIds || []);
                  const sessionOptions = (event.sessions || []).filter((session) => allowed.has(session.id) && isSessionBookableForPurchase(session));
                  const lockedSessionIds = new Set(
                    cartItemsForEvent
                      .filter((item) => item.ticketTypeId === ticket.id)
                      .flatMap((item) => item.sessionIds),
                  );
                  const selectableSessionOptions = sessionOptions.filter((session) => !lockedSessionIds.has(session.id));
                  const effectiveSelectedSessionIds = selectedSessionIds.filter((sessionId) => !lockedSessionIds.has(sessionId));
                  const selectionInCart = isSelectionInCart(ticket.id, effectiveSelectedSessionIds);
                  return (
                    <div key={ticket.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{ticket.name}</p>
                          <p className="text-sm text-slate-600">{formatCurrencyFromCents(ticket.priceCents, event.currency)} per session</p>
                                                  </div>
                        <button type="button" className={`rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                          selectionInCart ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-[#1F8F5F] hover:bg-[#1F8F5F]'
                        }`} disabled={!effectiveSelectedSessionIds.length || !selectableSessionOptions.length || selectionInCart} onClick={() => {
                          const wasAdded = onAddToCart(ticket.id, effectiveSelectedSessionIds);
                          if (wasAdded) {
                            setSpecificSessionDraft((prev) => ({ ...prev, [ticket.id]: [] }));
                          }
                        }}>
                          {selectionInCart ? 'In cart' : 'Add to cart'}
                        </button>
                      </div>
                      <div className="mt-2 grid gap-1">
                        {!sessionOptions.length && (
                          <p className="text-xs text-rose-700">Not available to book.</p>
                        )}
                        {sessionOptions.map((session) => (
                          <label key={session.id} className={`flex items-center gap-2 text-sm ${lockedSessionIds.has(session.id) ? 'text-slate-400' : 'text-slate-700'}`}>
                            <input
                              type="checkbox"
                              checked={effectiveSelectedSessionIds.includes(session.id)}
                              disabled={lockedSessionIds.has(session.id)}
                              onChange={(e) =>
                                setSpecificSessionDraft((prev) => ({
                                  ...prev,
                                  [ticket.id]: e.target.checked
                                    ? [...(prev[ticket.id] || []), session.id]
                                    : (prev[ticket.id] || []).filter((id) => id !== session.id),
                                }))
                              }
                            />
                            <span>
                              {new Date(session.startDateTime).toLocaleString('en-GB')}
                              {lockedSessionIds.has(session.id) ? ' (already in cart)' : ''}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          {message && <p className="mt-4 text-sm text-emerald-700">{message}</p>}
          {hasEventItemInCart && (
            <Link to={`/book/${event._id}/checkout`} className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#1F8F5F] hover:underline">
              Go to checkout
            </Link>
          )}
        </section>
      </div>
  );

  if (isParentLoggedIn) {
    return (
      <ParentPortalShell title={event.title} subtitle="Book activity" showHeaderCard={false}>
        {content}
      </ParentPortalShell>
    );
  }

  return (
    <main className="min-h-screen bg-[#f3f4f8]">
      <PublicClubHeader
        clubName={event.clubProfile?.clubName || 'After-school club'}
        clubSlug={event.clubProfile?.publicSlug}
        clubDescription={event.clubProfile?.description}
        logoUrl={event.clubProfile?.logoUrl}
        showClubTitle={event.clubProfile?.showClubTitle !== false}
        cartHref="/cart"
      />
      {content}
    </main>
  );
};

export default PublicBookingPage;
